// =====================================================================
// DoseApplyDialog (EHR Protocolos / S63) — registra a aplicação de uma dose:
// FOTO (upload) + ASSINATURA do paciente (canvas nativo → PNG) + observação.
// Ao salvar, o backend baixa o estoque (se houver produto vinculado).
// =====================================================================
import React, { useState, useRef, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, CircularProgress
} from "@material-ui/core";
import { toast } from "react-toastify";
import { applyDose, protocolErrorMessage } from "../../services/protocolService";
import useIsMobile from "../../utils/useIsMobile";

const CANVAS_W = 460;
const CANVAS_H = 160;

const DoseApplyDialog = ({ open, patientId, protocol, dose, onClose, onApplied }) => {
  const isMobile = useIsMobile();
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasSig = useRef(false);

  // limpa + pinta o canvas de branco quando abre
  useEffect(() => {
    if (open) {
      setPhoto(null); setPhotoPreview(null); setNotes("");
      hasSig.current = false;
      // espera o canvas montar
      setTimeout(() => {
        const c = canvasRef.current;
        if (c) {
          const ctx = c.getContext("2d");
          ctx.fillStyle = "#fff";
          ctx.fillRect(0, 0, c.width, c.height);
        }
      }, 50);
    }
  }, [open]);

  const coords = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches && e.touches[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: (cx - r.left) * (c.width / r.width), y: (cy - r.top) * (c.height / r.height) };
  };
  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = coords(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = coords(e);
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    hasSig.current = true;
  };
  const endDraw = () => { drawing.current = false; };
  const clearSig = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    hasSig.current = false;
  };

  const handlePhoto = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const getSignatureFile = () =>
    new Promise((resolve) => {
      if (!hasSig.current || !canvasRef.current) return resolve(null);
      canvasRef.current.toBlob(
        (blob) => resolve(blob ? new File([blob], "assinatura.png", { type: "image/png" }) : null),
        "image/png"
      );
    });

  const handleApply = async () => {
    setSaving(true);
    try {
      const signature = await getSignatureFile();
      const res = await applyDose(patientId, protocol.id, dose.doseNumber, { photo, signature, notes });
      if (res && res.stockWarning) toast.warning(res.stockWarning);
      else toast.success("Dose registrada.");
      onApplied && onApplied();
    } catch (err) {
      toast.error(protocolErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        Aplicar dose {dose ? `${dose.doseNumber}` : ""}{protocol ? ` — ${protocol.name}` : ""}
      </DialogTitle>
      <DialogContent>
        {protocol && protocol.productId && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Ao registrar, será baixado <strong>{Number(protocol.stockQtyPerDose) || 1}</strong> de{" "}
            <strong>{protocol.productName || "estoque"}</strong>.
          </Typography>
        )}

        <Typography variant="subtitle2" style={{ marginTop: 8 }}>Foto da aplicação (opcional)</Typography>
        <Box display="flex" alignItems="center" style={{ gap: 12, margin: "4px 0 8px" }}>
          <Button variant="outlined" component="label" size="small">
            Escolher foto
            <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={handlePhoto} />
          </Button>
          {photoPreview && (
            <img src={photoPreview} alt="prévia" style={{ height: 56, borderRadius: 6, border: "1px solid #ddd" }} />
          )}
        </Box>

        <Typography variant="subtitle2">Assinatura do paciente (opcional)</Typography>
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          style={{ width: "100%", height: CANVAS_H, border: "1px solid #bbb", borderRadius: 6, touchAction: "none", background: "#fff" }}
          onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
        />
        <Box display="flex" justifyContent="flex-end" style={{ marginBottom: 8 }}>
          <Button size="small" onClick={clearSig}>Limpar assinatura</Button>
        </Box>

        <TextField label="Observação (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)}
          fullWidth margin="dense" multiline minRows={2} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleApply} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}>Registrar dose</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DoseApplyDialog;
